/**
 * Registers the V2 lane's socket handlers — ONCE per socket instance.
 *
 * The effect is keyed on `[socket]` only. Each handler body is
 * `liveGameStore.getState().applyMessage(type, data)`, which closes over
 * nothing from React, so it can never go stale and never needs re-registering
 * (PlayChess re-creates ~45 closures whenever [username, accessToken, socket]
 * change — index.js L3671).
 *
 * The SDK has NO unregister API (node_modules/cc-client-sdk/lib/index.js
 * L124-176), so handlers outlive the screen. That is harmless here: they only
 * write the store. Handler keys are `PlayChessV2:<type>` and therefore never
 * collide with the old lane's `PlayChess:<type>`.
 *
 * F10 (fix F-B, 2026-10-06) — registration could go quiet after the FIRST
 * connection. Three causes, all in the `fetch_game_state` effect below:
 *  (a) `fetchedSockets` was a once-per-SOCKET-OBJECT guard, so a reconnect on
 *      the SAME object (server blip, laptop sleep, the SDK's own
 *      `force_reconnect`) never sent a second fetch — the object survives the
 *      reconnect, the guard never resets;
 *  (b) an `auth_complete` that resolved NO user id (`socket.username === ''`,
 *      the SDK's own listener on the same event runs FIRST and sets it — SDK
 *      `lib/index.js` L47-58) still called `sendFetch()`, burning the guard on
 *      an empty-id fetch;
 *  (c) the `auth_complete` listener was attached only when the username was
 *      missing AT MOUNT, so a reconnect's fresh `auth_complete` on an
 *      already-authed socket was never even heard.
 * Fixed by making `fetched` a flag LOCAL to this effect's closure (so it
 * tracks the CONNECTION, not the object), gating `sendFetch` on a non-empty
 * username, and always attaching `auth_complete`/`disconnect`/`close`. A
 * watchdog then resends up to 3 times (5 s apart) if nothing acknowledges the
 * request, surfacing the outcome on `Strip/authStatus`'s `registered` /
 * `watchdogExhausted` (`Strip/model.ts`'s fourth face).
 */
import {useEffect} from 'react';

import {useSocket} from '@/Hooks/UseSocket';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {INBOUND, OUTBOUND} from '@/Store/liveGame/messages';
import {bridgeSetTournamentPlay} from '@/Store/liveGame/gamePlayStoreWrites';
import {consumeChallengePacket} from '@/Store/liveGame/handshakes';
import {liveGameStore, setSocketProvider} from '@/Store/liveGameStore';

import {useSocketAuth} from '../Strip/authStatus';

/** one registration pass per socket instance, even across screen remounts */
const registeredSockets = new WeakMap<object, boolean>();
/**
 * The `challengePacket` handshake is consumed ONCE per tab: it is a one-shot
 * key another screen left behind (PlayChess L3870-3899 nulls it the same way),
 * and a remount must not re-adopt terms that are already settled.
 */
let challengePacketConsumed = false;

let registeredInTab = false;

/**
 * F10: the watchdog's acknowledgement set — the two socket messages the core
 * handles as the answer to `fetch_game_state` (`Store/liveGame/messages.ts`,
 * the 'On fetch'/'On reconnect' `start_game_response` restore and the
 * empty-table `no_ongoing_game` reset). RAW listeners, deliberately
 * UNDECODED — the event firing at all is proof the server is talking to us,
 * which is all the watchdog needs to know.
 */
const FETCH_RESULT_TYPES = ['start_game_response', 'no_ongoing_game'];
/** 5 s per attempt, 3 resends max, then the watchdog gives up (F10). */
const WATCHDOG_MS = 5000;
const MAX_RESENDS = 3;

/**
 * True once this tab has registered V2 handlers. The route switch uses it: a
 * request for the OLD lane afterwards has to reload the tab, because the V2
 * handlers cannot be removed and would keep writing the V2 store.
 */
export const wereV2HandlersRegistered = (): boolean => registeredInTab;

const registerAll = (socket: any): void => {
  Object.keys(INBOUND).forEach(type => {
    const schema = INBOUND[type];
    // `join_tournament_conflict` is deliberately schema-less (plain JSON),
    // exactly as PlayChess L1500-1506 leaves it.
    if (schema) {
      socket.registerMessageType(type, schema);
    }
    socket.registerMessageHandler(
      type,
      function (_socket: any, data: any) {
        liveGameStore.getState().applyMessage(type, data);
      },
      `PlayChessV2:${type}`,
    );
  });

  // Outbound-only types still need their schema registered or send_msg ships
  // an unencoded object (SDK lib/index.js L88-90).
  Object.keys(OUTBOUND).forEach(type => {
    const schema = OUTBOUND[type];
    if (schema && !INBOUND[type]) {
      socket.registerMessageType(type, schema);
    }
  });
};

/**
 * Identity mirrors what PlayChess's own profile callback extracts (L481-512:
 * pk, username, custom_rules, is_staff, gameplay_rating L490), but it is
 * SOURCED differently: PlayChess re-fetches the profile itself, while V2 reads
 * the user payload `Application.tsx` already passes to every screen
 * (`props.data.data.user`, `props.dashboardData.user`), falling back to
 * `useLoggedInUserStore.userData` (the same array). NOTHING in the V2 tree
 * calls `getProfile`.
 * `me` is String(pk) — the socket logs in with the user id, not the username.
 */
export const readSession = (userFromProps: any, userData: any) => {
  const source = Array.isArray(userFromProps)
    ? userFromProps[0]
    : userFromProps ?? (Array.isArray(userData) ? userData[0] : userData);
  const fields = source?.fields ?? source;
  const pk = source?.pk ?? fields?.user_id ?? fields?.id;
  const rating = Number(fields?.gameplay_rating);
  return {
    me: pk === undefined || pk === null ? '' : String(pk),
    displayName: String(fields?.username ?? ''),
    customRules: Array.isArray(fields?.custom_rules) ? fields.custom_rules : [],
    isStaff: Boolean(fields?.is_staff),
    rating: Number.isFinite(rating) ? rating : null,
    /**
     * The server-side lobby A/B assignment (M2 spec §1.3). The payload does not
     * carry it today, so this is null for everyone and the resolver falls
     * through to the percentage bucket — the field exists so turning it on is a
     * backend change only.
     */
    lobbyVariant: fields?.lobby_variant ? String(fields.lobby_variant) : null,
  };
};

export function useGameplayHandlers(props: any): void {
  const socket = useSocket();
  const userData = useLoggedInUserStore(s => s.userData);
  // Both shapes the navigator passes carry the same `user[0].fields` array;
  // `PlayChessSwitch` reads `is_staff` off exactly this pair (index.tsx L27-30).
  const userFromProps = props?.data?.data?.user ?? props?.dashboardData?.user;
  const isBeginner = Boolean(props?.isBeginnerPlayer);

  // Identity lives in its OWN effect: the dashboard payload routinely resolves
  // after the socket does, and a [socket]-only effect would freeze
  // displayName / customRules / isStaff at their empty defaults for the whole
  // session (customRules gates the thinking-time and touch-piece rules).
  useEffect(() => {
    const session = readSession(userFromProps, userData);
    const known = liveGameStore.getState().session.me;
    liveGameStore.getState().setSession({
      ...session,
      // `isBeginnerPlayer` is a prop of its own (Application.tsx L1169), not a
      // field of the user payload.
      isBeginner,
      // never clobber an id we already know: a later payload may carry the
      // profile fields without a pk.
      me: session.me || String(socket?.username || '') || known,
    });
  }, [socket, userFromProps, userData, isBeginner]);

  useEffect(() => {
    if (!socket) {
      return;
    }
    setSocketProvider(() => socket);

    if (!registeredSockets.has(socket)) {
      registeredSockets.set(socket, true);
      registeredInTab = true;
      // Registration needs no username — do it synchronously so a message
      // that lands during authentication is not lost.
      registerAll(socket);
    }

    // The `challengePacket` handshake (flow 'challengeAccept', PlayChess
    // L3870-3899). AFTER registration, so the `start_game_response` the accept
    // will trigger already has a handler. Nothing is SENT: the app-wide
    // `Components/ChallengeComponent` sent `challenge_accept_response` before it
    // navigated here — this only records the terms and nulls the key.
    if (!challengePacketConsumed) {
      challengePacketConsumed = true;
      consumeChallengePacket()
        .then(packet => {
          liveGameStore.getState().actions.adoptChallengePacket(packet);
        })
        .catch(() => {
          // the app store is unavailable — the game still starts on its own
        });
    }

    // F10 — PER-CONNECTION state, local to this effect's closure (NOT a
    // module-level WeakMap) so a reconnect on the SAME socket object starts
    // it fresh: `disconnect`/`close` reset it below.
    let fetched = false;
    let resends = 0;
    let watchdogTimer: any = null;

    const clearWatchdog = () => {
      if (watchdogTimer) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
      }
    };

    // Re-sends the SAME fetch up to MAX_RESENDS times, 5 s apart, then gives
    // up — the strip's fourth face (`Strip/model.ts`) is what tells the
    // player.
    const armWatchdog = (me: string) => {
      clearWatchdog();
      watchdogTimer = setTimeout(() => {
        if (resends >= MAX_RESENDS) {
          useSocketAuth.getState().setWatchdogExhausted(true);
          return;
        }
        resends += 1;
        liveGameStore.getState().actions.fetchGameState(me);
        armWatchdog(me);
      }, WATCHDOG_MS);
    };

    // No decoding — the RAW event is the ack (see FETCH_RESULT_TYPES above).
    const onAck = () => {
      clearWatchdog();
      useSocketAuth.getState().setRegistered(true);
    };

    const sendFetch = () => {
      if (fetched) {
        return;
      }
      fetched = true;
      // read the store, not a captured value: the session effect above may
      // have landed the profile id in between.
      const me = String(
        socket.username || liveGameStore.getState().session.me || '',
      );
      if (me) {
        liveGameStore.getState().setSession({me});
      }
      resends = 0;
      useSocketAuth.getState().setRegistered(false);
      useSocketAuth.getState().setWatchdogExhausted(false);
      liveGameStore.getState().actions.fetchGameState(me);
      // PlayChess L3922 releases the tournament gate right after the fetch.
      bridgeSetTournamentPlay(false);
      armWatchdog(me);
    };

    const onAuthComplete = () => {
      // The SDK's OWN 'auth_complete' listener stamps `socket.username`
      // before this one runs — it is registered first, on the same event
      // (lib/index.js L47-58). An id that resolves empty (b) means `fetched`
      // stays false, so the next auth that DOES resolve one still fetches.
      if (socket.username) {
        sendFetch();
      }
    };

    const onDisconnect = () => {
      fetched = false;
      resends = 0;
      clearWatchdog();
      useSocketAuth.getState().setRegistered(false);
      useSocketAuth.getState().setWatchdogExhausted(false);
    };

    // The server's 'force_reconnect' nudge: the SDK answers it with a
    // Manager-level `connect()`, which is a NO-OP while the transport still
    // looks open (a zombie socket after sleep/wake), so neither 'disconnect'
    // nor a fresh 'auth_complete' may ever follow. Re-arm the guard AND
    // re-register right away; a dead transport buffers the emit until
    // socket.io's own ping timeout reconnects it (F-B review).
    const onForceReconnect = () => {
      onDisconnect();
      if (socket.username) {
        sendFetch();
      }
    };

    FETCH_RESULT_TYPES.forEach(type => socket.on?.(type, onAck));

    if (socket.username) {
      sendFetch();
    }

    // ALWAYS attached (c) — not only when the username is missing at mount:
    // a reconnect fires 'auth_complete' again on this SAME socket object, and
    // reacting to that is the whole point of this fix.
    socket.on?.('auth_complete', onAuthComplete);
    socket.on?.('disconnect', onDisconnect);
    socket.on?.('close', onDisconnect);
    socket.on?.('force_reconnect', onForceReconnect);

    return () => {
      clearWatchdog();
      try {
        socket.off?.('auth_complete', onAuthComplete);
      } catch {
        // ignore cleanup failures
      }
      try {
        socket.off?.('disconnect', onDisconnect);
      } catch {
        // ignore cleanup failures
      }
      try {
        socket.off?.('close', onDisconnect);
      } catch {
        // ignore cleanup failures
      }
      try {
        socket.off?.('force_reconnect', onForceReconnect);
      } catch {
        // ignore cleanup failures
      }
      FETCH_RESULT_TYPES.forEach(type => {
        try {
          socket.off?.(type, onAck);
        } catch {
          // ignore cleanup failures
        }
      });
    };
  }, [socket]);
}

export default useGameplayHandlers;
